import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import {
  CheckCircle, AlertTriangle, Clock, Loader2, Lock,
  ExternalLink, Plus, Minus, Building2, CreditCard, Landmark, FileText,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BuildingBrand {
  merchant: string;
  displayName: string;
  logoUrl: string;
  iconUrl: string;
  primaryColor: string;
  accentColor: string;
  secondaryColor: string;
  supportEmail: string;
  termsUrl: string;
  privacyUrl: string;
}

interface SessionData {
  checkoutSessionId: string;
  status: string;
  planId: string;
  planName: string;
  planDescription: string;
  amountCents: number;
  unitAmountCents: number;
  currency: string;
  billingMode: string;
  billingInterval: string;
  quantity: number;
  allowQuantity: boolean;
  paperBilling: boolean;
  customerEmail: string;
  customerName: string | null;
  customerCompany: string | null;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  brand: BuildingBrand;
}

// 'debit' and 'bank' both use Plaid + Increase ACH — same flow, different Plaid UI hint
type PaymentMethod = 'debit' | 'bank' | 'paper';
type PageState = 'loading' | 'ready' | 'processing' | 'paid' | 'pending_invoice' | 'expired' | 'cancelled' | 'failed' | 'error';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtCurrency = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

const intervalLabel: Record<string, string> = {
  one_time: 'one-time',
  monthly: '/ mo',
  quarterly: '/ quarter',
  annual: '/ yr',
};

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : '/api/v1');

function statusToPage(status: string): PageState {
  if (status === 'paid') return 'paid';
  if (status === 'expired') return 'expired';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'failed') return 'failed';
  if (status === 'pending_invoice') return 'pending_invoice';
  return 'ready';
}

// ─── Plaid ACH sub-component ──────────────────────────────────────────────────

interface PlaidButtonProps {
  sessionKey: string;
  quantity: number;
  accentColor: string;
  label?: string;
  icon?: React.ReactNode;
  onSuccess: () => void;
  onError: (msg: string) => void;
  onProcessing: () => void;
}

const PlaidACHButton: React.FC<PlaidButtonProps> = ({
  sessionKey, quantity, accentColor, label, icon, onSuccess, onError, onProcessing,
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    setFetching(true);
    fetch(`${API_BASE}/billing/building/public/sessions/${sessionKey}/pay/plaid-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(r => r.json())
      .then(j => {
        if (j.success) setLinkToken(j.data.linkToken);
        else onError(j.error || 'Failed to initialize bank link');
      })
      .catch(() => onError('Failed to reach payment server'))
      .finally(() => setFetching(false));
  }, [sessionKey, onError]);

  const onPlaidSuccess = useCallback((publicToken: string, metadata: any) => {
    const accountId = metadata?.account_id || metadata?.accounts?.[0]?.id;
    if (!accountId) { onError('No bank account selected'); return; }

    onProcessing();
    fetch(`${API_BASE}/billing/building/public/sessions/${sessionKey}/pay/ach`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicToken, accountId, quantity }),
    })
      .then(r => r.json())
      .then(j => {
        if (j.success) onSuccess();
        else onError(j.error || 'ACH transfer failed');
      })
      .catch(() => onError('Payment failed — please try again'));
  }, [sessionKey, quantity, onSuccess, onError, onProcessing]);

  const { open, ready } = usePlaidLink({
    token: linkToken || '',
    onSuccess: onPlaidSuccess,
    onExit: (err) => { if (err) onError(err.display_message || 'Bank link cancelled'); },
  });

  return (
    <button
      className="bld-btn-primary"
      style={{ background: accentColor, width: '100%', opacity: (!ready || fetching) ? 0.6 : 1 }}
      disabled={!ready || fetching}
      onClick={() => open()}
    >
      {fetching ? (
        <><Loader2 size={16} style={{ marginRight: 8, animation: 'bld-spin 1s linear infinite' }} />Connecting…</>
      ) : (
        <>{icon ?? <Landmark size={16} style={{ marginRight: 8 }} />}{label ?? 'Connect Bank Account'}</>
      )}
    </button>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const BuildingCheckout: React.FC = () => {
  const { sessionKey } = useParams<{ sessionKey: string }>();
  const [searchParams] = useSearchParams();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [session, setSession] = useState<SessionData | null>(null);
  const [brand, setBrand] = useState<BuildingBrand>({
    merchant: 'building', displayName: 'Building', logoUrl: '', iconUrl: '',
    primaryColor: '#0A0A0A', secondaryColor: '#F5F5F0', accentColor: '#1A56DB',
    supportEmail: 'building@redexct.xyz', termsUrl: '', privacyUrl: '',
  });
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('debit');
  const [error, setError] = useState('');

  // Handle ?status= redirect from payment processors
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') { setPageState('paid'); return; }
    if (status === 'cancel')  { setPageState('cancelled'); return; }
  }, [searchParams]);

  // Fetch session
  useEffect(() => {
    if (!sessionKey || searchParams.get('status')) return;

    fetch(`${API_BASE}/billing/building/public/sessions/${sessionKey}`)
      .then(r => r.json())
      .then(json => {
        if (!json.success) { setError(json.error || 'Session not found'); setPageState('error'); return; }
        const s: SessionData = json.data;
        setSession(s);
        if (s.brand) setBrand(s.brand);
        setQuantity(s.quantity || 1);
        if (s.paperBilling) setPaymentMethod('paper');
    else setPaymentMethod('debit');
        setPageState(statusToPage(s.status));
      })
      .catch(() => { setError('Unable to load checkout session'); setPageState('error'); });
  }, [sessionKey, searchParams]);

  const totalCents = session?.allowQuantity
    ? (session.unitAmountCents || 0) * quantity
    : (session?.amountCents || 0);

  const handlePaperInvoice = () => {
    if (!sessionKey) return;
    setPageState('processing');
    fetch(`${API_BASE}/billing/building/public/sessions/${sessionKey}/pay/paper`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(r => r.json())
      .then(j => {
        if (j.success) setPageState('pending_invoice');
        else { setError(j.error || 'Failed to submit invoice request'); setPageState('ready'); }
      })
      .catch(() => { setError('Request failed — please try again'); setPageState('ready'); });
  };

  const cssVars = {
    '--bld-primary': brand.primaryColor,
    '--bld-accent': brand.accentColor,
    '--bld-secondary': brand.secondaryColor,
  } as React.CSSProperties;

  // ── Terminal states ────────────────────────────────────────────────────────

  if (pageState === 'loading') return (
    <div style={cssVars} className="bld-shell">
      <div className="bld-card bld-card--center">
        <Loader2 size={32} className="bld-spin" style={{ color: brand.accentColor }} />
        <p className="bld-muted" style={{ marginTop: 12 }}>Loading checkout…</p>
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'processing') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <Loader2 size={32} className="bld-spin" style={{ color: brand.accentColor }} />
        <h2 className="bld-heading" style={{ marginTop: 16 }}>Processing payment…</h2>
        <p className="bld-muted">Please do not close this page.</p>
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'error') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <AlertTriangle size={28} style={{ color: '#DC2626', marginBottom: 12 }} />
        <h2 className="bld-heading">Something went wrong</h2>
        <p className="bld-muted">{error || 'This checkout link is invalid or has been removed.'}</p>
        <p className="bld-muted" style={{ marginTop: 8, fontSize: 13 }}>
          Contact <a href={`mailto:${brand.supportEmail}`} style={{ color: brand.accentColor }}>{brand.supportEmail}</a>
        </p>
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'paid') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <CheckCircle size={48} style={{ color: '#16A34A', marginBottom: 16 }} />
        <h2 className="bld-heading">Payment confirmed</h2>
        <p className="bld-muted" style={{ marginTop: 4 }}>
          {session?.planName} — {fmtCurrency(totalCents, session?.currency)}
        </p>
        {session?.customerEmail && (
          <p className="bld-muted" style={{ marginTop: 8, fontSize: 13 }}>
            A receipt will be sent to <strong>{session.customerEmail}</strong>
          </p>
        )}
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'pending_invoice') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <FileText size={40} style={{ color: brand.accentColor, marginBottom: 16 }} />
        <h2 className="bld-heading">Invoice requested</h2>
        <p className="bld-muted">
          Our team will send a paper invoice to <strong>{session?.customerEmail}</strong> within 1 business day.
        </p>
        <p className="bld-muted" style={{ marginTop: 8, fontSize: 13 }}>
          Questions? <a href={`mailto:${brand.supportEmail}`} style={{ color: brand.accentColor }}>{brand.supportEmail}</a>
        </p>
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'expired') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <Clock size={36} style={{ color: '#92400E', marginBottom: 12 }} />
        <h2 className="bld-heading">This link has expired</h2>
        <p className="bld-muted">Payment links are valid for 72 hours. Contact {brand.displayName} for a new link.</p>
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'cancelled') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <AlertTriangle size={32} style={{ color: '#6B7280', marginBottom: 12 }} />
        <h2 className="bld-heading">Checkout cancelled</h2>
        <p className="bld-muted">This payment was cancelled. Contact {brand.displayName} if you have questions.</p>
      </div>
      <BuildingStyles />
    </div>
  );

  if (pageState === 'failed') return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />
      <div className="bld-card bld-card--center">
        <AlertTriangle size={32} style={{ color: '#DC2626', marginBottom: 12 }} />
        <h2 className="bld-heading">Payment failed</h2>
        <p className="bld-muted">There was a problem processing this payment. Please try again or contact support.</p>
        <p className="bld-muted" style={{ marginTop: 8, fontSize: 13 }}>
          <a href={`mailto:${brand.supportEmail}`} style={{ color: brand.accentColor }}>{brand.supportEmail}</a>
        </p>
      </div>
      <BuildingStyles />
    </div>
  );

  // ── Ready — main checkout UI ───────────────────────────────────────────────

  const isPaperOnly = session?.paperBilling === true;

  return (
    <div style={cssVars} className="bld-shell">
      <BrandHeader brand={brand} />

      <div className="bld-card">
        {/* Plan summary */}
        <div className="bld-plan-header">
          <div>
            <div className="bld-plan-name">{session?.planName}</div>
            {session?.planDescription && (
              <div className="bld-plan-desc">{session.planDescription}</div>
            )}
          </div>
          <div className="bld-plan-price">
            {isPaperOnly ? (
              <span className="bld-price-custom">Custom</span>
            ) : (
              <>
                <span className="bld-price-amount">
                  {fmtCurrency(session?.unitAmountCents || 0, session?.currency)}
                </span>
                <span className="bld-price-interval">
                  {intervalLabel[session?.billingInterval || 'one_time'] || ''}
                  {session?.allowQuantity ? ' / property' : ''}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Quantity selector */}
        {session?.allowQuantity && (
          <>
            <div className="bld-divider" />
            <div className="bld-qty-row">
              <span className="bld-qty-label">Number of properties</span>
              <div className="bld-qty-controls">
                <button
                  className="bld-qty-btn"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  aria-label="Decrease"
                >
                  <Minus size={14} />
                </button>
                <span className="bld-qty-value">{quantity}</span>
                <button
                  className="bld-qty-btn"
                  onClick={() => setQuantity(q => q + 1)}
                  aria-label="Increase"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            <div className="bld-total-row">
              <span className="bld-total-label">Total due today</span>
              <span className="bld-total-amount" style={{ color: brand.primaryColor }}>
                {fmtCurrency(totalCents, session?.currency)}
                <span className="bld-total-interval"> {intervalLabel[session?.billingInterval || 'one_time']}</span>
              </span>
            </div>
          </>
        )}

        <div className="bld-divider" />

        {/* Customer info */}
        <div className="bld-customer-row">
          <span className="bld-meta-label">Billing to</span>
          <div style={{ textAlign: 'right' }}>
            {session?.customerName && <div className="bld-meta-value">{session.customerName}</div>}
            {session?.customerCompany && <div className="bld-meta-value bld-meta-company">{session.customerCompany}</div>}
            <div className="bld-meta-value bld-meta-email">{session?.customerEmail}</div>
          </div>
        </div>

        <div className="bld-secure-notice">
          <Lock size={12} />
          <span>Secure payment processed by REDEX Capital</span>
        </div>

        <div className="bld-divider" />

        {/* Payment method selection — debit card and bank transfer both settle via Increase ACH */}
        {!isPaperOnly && (
          <div className="bld-method-tabs">
            <button
              className={`bld-method-tab ${paymentMethod === 'debit' ? 'bld-method-tab--active' : ''}`}
              style={paymentMethod === 'debit' ? { borderColor: brand.accentColor, color: brand.accentColor } : {}}
              onClick={() => setPaymentMethod('debit')}
            >
              <CreditCard size={15} style={{ marginRight: 6 }} />
              Debit Card
            </button>
            <button
              className={`bld-method-tab ${paymentMethod === 'bank' ? 'bld-method-tab--active' : ''}`}
              style={paymentMethod === 'bank' ? { borderColor: brand.accentColor, color: brand.accentColor } : {}}
              onClick={() => setPaymentMethod('bank')}
            >
              <Landmark size={15} style={{ marginRight: 6 }} />
              Bank Transfer
            </button>
          </div>
        )}

        {/* Debit card — Plaid Link with card-focused hint, settled via Increase ACH */}
        {paymentMethod === 'debit' && !isPaperOnly && sessionKey && (
          <div className="bld-method-body">
            <p className="bld-method-hint">
              Connect your debit card via Plaid. Payment is debited from your linked account and settled via Increase.
            </p>
            {error && <p className="bld-error">{error}</p>}
            <PlaidACHButton
              sessionKey={sessionKey}
              quantity={quantity}
              accentColor={brand.accentColor}
              label="Connect Debit Card"
              icon={<CreditCard size={16} style={{ marginRight: 8 }} />}
              onSuccess={() => setPageState('paid')}
              onError={(msg) => { setError(msg); }}
              onProcessing={() => { setError(''); setPageState('processing'); }}
            />
          </div>
        )}

        {/* Bank transfer — Plaid Link with bank account hint, settled via Increase ACH */}
        {paymentMethod === 'bank' && !isPaperOnly && sessionKey && (
          <div className="bld-method-body">
            <p className="bld-method-hint">
              Connect your bank account securely via Plaid. Funds are debited via ACH and typically settle in 1–3 business days.
            </p>
            {error && <p className="bld-error">{error}</p>}
            <PlaidACHButton
              sessionKey={sessionKey}
              quantity={quantity}
              accentColor={brand.accentColor}
              label="Connect Bank Account"
              icon={<Landmark size={16} style={{ marginRight: 8 }} />}
              onSuccess={() => setPageState('paid')}
              onError={(msg) => { setError(msg); }}
              onProcessing={() => { setError(''); setPageState('processing'); }}
            />
          </div>
        )}

        {/* Paper invoice */}
        {isPaperOnly && (
          <div className="bld-method-body">
            <p className="bld-method-hint">
              Our team will prepare a custom invoice and send it to your email within 1 business day.
            </p>
            {error && <p className="bld-error">{error}</p>}
            <button
              className="bld-btn-primary"
              style={{ background: brand.accentColor, width: '100%' }}
              onClick={handlePaperInvoice}
            >
              <FileText size={16} style={{ marginRight: 8 }} />
              Request Invoice
            </button>
          </div>
        )}
      </div>

      <BuildingFooter brand={brand} />
      <BuildingStyles />
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const BrandHeader: React.FC<{ brand: BuildingBrand }> = ({ brand }) => (
  <div className="bld-header">
    {brand.logoUrl
      ? <img src={brand.logoUrl} alt={brand.displayName} className="bld-logo" />
      : <div className="bld-logo-text" style={{ color: brand.primaryColor }}>
          <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {brand.displayName}
        </div>
    }
  </div>
);

const BuildingFooter: React.FC<{ brand: BuildingBrand }> = ({ brand }) => (
  <div className="bld-footer">
    <div className="bld-footer-links">
      {brand.termsUrl && (
        <a href={brand.termsUrl} target="_blank" rel="noopener noreferrer" className="bld-footer-link">
          Terms <ExternalLink size={11} style={{ marginLeft: 3 }} />
        </a>
      )}
      {brand.privacyUrl && (
        <a href={brand.privacyUrl} target="_blank" rel="noopener noreferrer" className="bld-footer-link">
          Privacy <ExternalLink size={11} style={{ marginLeft: 3 }} />
        </a>
      )}
      <a href={`mailto:${brand.supportEmail}`} className="bld-footer-link">{brand.supportEmail}</a>
    </div>
    <p className="bld-footer-attribution">Payments powered by <span style={{ color: '#6B7280' }}>REDEX Capital</span></p>
  </div>
);

const BuildingStyles: React.FC = () => (
  <style>{`
    .bld-shell {
      min-height: 100vh;
      background: var(--bld-secondary, #F5F5F0);
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 40px 16px 80px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .bld-header { margin-bottom: 28px; }
    .bld-logo { height: 36px; object-fit: contain; }
    .bld-logo-text {
      font-size: 20px; font-weight: 700; letter-spacing: -0.5px;
      display: flex; align-items: center;
    }
    .bld-card {
      background: #fff;
      border-radius: 12px;
      border: 1px solid #E5E7EB;
      padding: 28px 32px;
      width: 100%;
      max-width: 480px;
      box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    }
    .bld-card--center { display: flex; flex-direction: column; align-items: center; text-align: center; }
    .bld-heading { font-size: 20px; font-weight: 600; color: #111827; margin: 0 0 8px; }
    .bld-muted { font-size: 14px; color: #6B7280; margin: 0; line-height: 1.5; }
    .bld-spin { animation: bld-spin 1s linear infinite; }
    @keyframes bld-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    /* Plan header */
    .bld-plan-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
    .bld-plan-name { font-size: 17px; font-weight: 600; color: #111827; }
    .bld-plan-desc { font-size: 13px; color: #6B7280; margin-top: 3px; line-height: 1.4; }
    .bld-plan-price { text-align: right; flex-shrink: 0; }
    .bld-price-amount { display: block; font-size: 24px; font-weight: 700; color: #111827; }
    .bld-price-interval { font-size: 12px; color: #9CA3AF; }
    .bld-price-custom { font-size: 18px; font-weight: 600; color: #111827; }

    /* Quantity */
    .bld-qty-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 4px 0 12px;
    }
    .bld-qty-label { font-size: 14px; color: #374151; font-weight: 500; }
    .bld-qty-controls { display: flex; align-items: center; gap: 12px; }
    .bld-qty-btn {
      width: 32px; height: 32px; border-radius: 8px;
      border: 1px solid #D1D5DB; background: #F9FAFB;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: #374151; transition: background 0.1s;
    }
    .bld-qty-btn:hover:not(:disabled) { background: #F3F4F6; }
    .bld-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .bld-qty-value { font-size: 18px; font-weight: 700; color: #111827; min-width: 28px; text-align: center; }
    .bld-total-row {
      display: flex; justify-content: space-between; align-items: baseline;
      padding: 8px 0 4px;
    }
    .bld-total-label { font-size: 13px; color: #6B7280; font-weight: 500; }
    .bld-total-amount { font-size: 22px; font-weight: 700; }
    .bld-total-interval { font-size: 12px; color: #9CA3AF; font-weight: 400; }

    /* Customer */
    .bld-customer-row {
      display: flex; justify-content: space-between; align-items: flex-start;
      gap: 16px; padding-bottom: 12px;
    }
    .bld-meta-label { font-size: 12px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.04em; padding-top: 2px; }
    .bld-meta-value { font-size: 13px; color: #374151; }
    .bld-meta-company { color: #6B7280; font-size: 12px; }
    .bld-meta-email { font-size: 12px; color: #6B7280; }
    .bld-secure-notice {
      display: flex; align-items: center; gap: 5px;
      font-size: 11px; color: #9CA3AF; padding-top: 8px;
    }

    /* Payment method tabs */
    .bld-method-tabs { display: flex; gap: 8px; margin-bottom: 16px; }
    .bld-method-tab {
      flex: 1; display: flex; align-items: center; justify-content: center;
      padding: 10px 8px; border-radius: 8px; border: 1.5px solid #E5E7EB;
      background: #F9FAFB; font-size: 13px; font-weight: 500; color: #6B7280;
      cursor: pointer; transition: all 0.15s;
    }
    .bld-method-tab:hover { border-color: #D1D5DB; background: #F3F4F6; }
    .bld-method-tab--active { background: #fff; font-weight: 600; }
    .bld-method-body { display: flex; flex-direction: column; gap: 12px; }
    .bld-method-hint { font-size: 13px; color: #6B7280; line-height: 1.5; margin: 0; }
    .bld-error { font-size: 13px; color: #DC2626; margin: 0; padding: 10px 12px; background: #FEF2F2; border-radius: 6px; }

    /* CTA button */
    .bld-btn-primary {
      display: inline-flex; align-items: center; justify-content: center;
      padding: 13px 24px; border-radius: 8px; border: none;
      font-size: 15px; font-weight: 600; color: #fff;
      text-decoration: none; cursor: pointer; transition: opacity 0.15s;
    }
    .bld-btn-primary:hover:not(:disabled) { opacity: 0.88; }
    .bld-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

    /* Divider */
    .bld-divider { border: none; border-top: 1px solid #F3F4F6; margin: 16px 0; }

    /* Footer */
    .bld-footer { margin-top: 28px; text-align: center; max-width: 480px; }
    .bld-footer-links { display: flex; justify-content: center; gap: 16px; margin-bottom: 6px; flex-wrap: wrap; }
    .bld-footer-link {
      font-size: 12px; color: #9CA3AF; text-decoration: none;
      display: inline-flex; align-items: center;
    }
    .bld-footer-link:hover { color: #6B7280; }
    .bld-footer-attribution { font-size: 11px; color: #D1D5DB; margin: 0; }
  `}</style>
);

export default BuildingCheckout;
